import { Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { AlertService } from '../../../core/ui/alert';
import { LoaderService } from '../../../core/ui/loader';
import { PartnerLiquidationListItem } from '../../../shared/models/partner-liquidation-list-item';
import { FunctionResponse } from '../../../shared/models/response';
import { convertFirestoreTimestampToDate } from '../../../shared/utils/date-converter';
import { PartnerLiquidationDetail, PartnerDocumentItem } from '../../../shared/models/partner-liquidation-detail';

@Injectable({
    providedIn: 'root'
})
export class PartnerLiquidationsService {
    constructor(private functions: Functions, private alertService: AlertService, private loaderService: LoaderService) {}

    async getPartnerLiquidations(): Promise<PartnerLiquidationListItem[] | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<void, FunctionResponse<PartnerLiquidationListItem[]>>(this.functions, 'getPartnerLiquidations');
            const result   = await callable();

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                return result.data.data.map(liq => ({
                    ...liq,
                    createdAt: convertFirestoreTimestampToDate(liq.createdAt)
                }));
            }

            this.alertService.error(result.data.message || 'No se pudieron obtener las liquidaciones.', 'Error');

            return null;
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al obtener liquidaciones del cliente:', error);

            this.alertService.error(error.message || 'Error desconocido al obtener liquidaciones.', 'Error');

            return null;
        }
    }

    async getPartnerLiquidationDetail(liquidationId: string): Promise<PartnerLiquidationDetail | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ liquidationId: string }, FunctionResponse<PartnerLiquidationDetail>>(this.functions, 'getPartnerLiquidationDetail');
            const result   = await callable({ liquidationId });

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const detail = result.data.data;

                return {
                    ...detail,
                    createdAt: convertFirestoreTimestampToDate(detail.createdAt as any) as any
                };
            }

            this.alertService.error(result.data.message || 'No se pudo obtener el detalle de la liquidación.', 'Error');
            return null;
        } catch(error: any) {
            this.loaderService.hide();
            console.error('Error al obtener detalle de liquidación:', error);
            this.alertService.error(error.message || 'Error desconocido al obtener el detalle.', 'Error');
            return null;
        }
    }

    async savePartnerLiquidationDocuments(liquidationId: string, invoices: PartnerDocumentItem[], deposits: PartnerDocumentItem[]): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ liquidationId: string; invoices: PartnerDocumentItem[]; deposits: PartnerDocumentItem[] }, FunctionResponse<null>>(this.functions, 'savePartnerLiquidationDocuments');
            const result   = await callable({ liquidationId, invoices, deposits });

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message || 'Documentos guardados correctamente.', 'Éxito');
                return true;
            }

            this.alertService.error(result.data.message || 'No se pudieron guardar los documentos.', 'Error');
            return false;
        } catch(error: any) {
            this.loaderService.hide();
            console.error('Error al guardar documentos de liquidación:', error);
            this.alertService.error(error.message || 'Error desconocido al guardar documentos.', 'Error');
            return false;
        }
    }
}
